<script>
import AscStatusMethodsMixin from './AscStatusMethodsMixin.vue'
import { isDate1AndDate2LessThanDays } from '@/utilities/dates'
import { v4 as uuidv4 } from 'uuid'

export default {
  mixins: [AscStatusMethodsMixin],
  data() {
    return {
      gridId: uuidv4(), // Add gridId as a key atribute to grid referencing this fixes the data refreshing issue.
      isFirstDataRendered: false,
    }
  },

  watch: {
    values() {
      this.gridId = uuidv4()
      this.handleRefreshColWidths()
    },
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.handleResizeHandler)
  },

  methods: {
    resizeGridColumns(params) {
      if (!params.api) return
      this.gridApi = params.api
      this.gridColumnApi = params.columnApi
      setTimeout(() => {
        // using timeout to avaoid zero width grid warning.
        this.gridApi.sizeColumnsToFit()
      }, 200)
      this.gridApi.refreshCells(params)
    },
    handleFirstDataRendered(params) {
      this.isFirstDataRendered = true
      // After aggrid upgrade the custom floating filters are not getting updated
      // Because of the new way of integration of custom filters so refreshHeader is added
      setTimeout(() => params.api.refreshHeader())
      this.resizeGridColumns(params)
      window.addEventListener('resize', this.handleResizeHandler)
      this.restoreGridModel(params)
    },
    restoreGridModel(params) {
      if (this.persistentGridId) {
        const filterModel = sessionStorage.getItem(
          `${this.persistentGridId}_filter`,
        )
        const columnState = sessionStorage.getItem(
          `${this.persistentGridId}_columnState`,
        )

        if (filterModel) {
          let gridData = []
          params.api.forEachNode(node => {
            gridData.push(node.data)
          })
          let storedFilterModel = JSON.parse(filterModel)
          let updatedFilterModel = {}
          let isFilterChanged = false
          for (let columnKey in storedFilterModel) {
            let filter = storedFilterModel[columnKey]
            if (!filter) continue
            if (filter.filterType === 'text' && filter.type === 'contains') {
              let textValues = filter.filter
                ?.toLowerCase()
                .split(',')
                .map(val => val.trim())
              let hasMatchingRow = gridData.some(row =>
                textValues.some(value =>
                  row[columnKey]?.toString().toLowerCase().includes(value),
                ),
              )
              if (!hasMatchingRow) {
                isFilterChanged = true
              } else {
                updatedFilterModel[columnKey] = filter
              }
            } else if (
              filter.filterType === 'number' &&
              filter.type === 'equals'
            ) {
              let filterValue = filter.filter
              let hasMatchingRow = gridData.some(row =>
                row[columnKey]?.toString().includes(filterValue),
              )
              if (!hasMatchingRow) {
                isFilterChanged = true
              } else {
                updatedFilterModel[columnKey] = filter
              }
            } else {
              updatedFilterModel[columnKey] = filter
            }
          }
          if (isFilterChanged) {
            sessionStorage.setItem(
              `${this.persistentGridId}_filter`,
              JSON.stringify(updatedFilterModel),
            )
            params.api.setFilterModel(updatedFilterModel)
          } else {
            params.api.setFilterModel(storedFilterModel)
          }
        }

        if (columnState) {
          const state = JSON.parse(columnState)
          if (Array.isArray(state)) {
            params.columnApi.applyColumnState({
              state: state,
            })
          }
        }
      }
    },
    handleGridFilterChange(params) {
      if (this.persistentGridId) {
        const filterModel = params.api.getFilterModel()
        if (this.isFirstDataRendered) {
          sessionStorage.setItem(
            `${this.persistentGridId}_filter`,
            JSON.stringify(filterModel),
          )
        }
      }
    },
    handleGridSortChange(params) {
      if (this.persistentGridId) {
        const columnState = params.columnApi.getColumnState()
        // Only Save sort order
        const sortState = columnState.map(state => ({
          colId: state.colId,
          sort: state.sort,
          sortIndex: state.sortIndex,
        }))

        sessionStorage.setItem(
          `${this.persistentGridId}_columnState`,
          JSON.stringify(sortState),
        )
      }
    },
    handleGridReady(params) {
      this.resizeGridColumns(params)
    },
    handleGridSizeChanged(params) {
      this.resizeGridColumns(params)
    },
    handleResizeHandler() {
      if (!this.gridApi) return
      const _that = this
      setTimeout(function () {
        _that.gridApi.sizeColumnsToFit()
      }, 200)
    },
    handleRefreshColWidths() {
      if (!this.gridApi) return
      const _that = this
      setTimeout(function () {
        _that.gridApi.sizeColumnsToFit()
      }, 200)
    },
    pad(num, size) {
      return String(num).padStart(size, '0')
    },
    sortDate(value1, value2) {
      return new Date(value1) - new Date(value2)
    },
    getUniqueItems(fieldName) {
      return Array.from(new Set(this.mappedValues?.map(s => s[fieldName])))
        .map(item => {
          return {
            text: item === null ? 'N/A' : item,
            value: item === null ? 'N/A' : item,
          }
        })
        .sort()
    },
    getUniqueCustomSortItems(fieldName, sortOptions) {
      return Array.from(new Set(this.mappedValues?.map(s => s[fieldName])))
        .map(item => {
          return {
            text: item === null ? 'N/A' : item,
            value: item === null ? 'N/A' : item,
            sortOrder: this.getCustomSortOrder(item, sortOptions),
          }
        })
        .sort((a, b) => a.sortOrder - b.sortOrder)
    },
    getCustomSortOrder(item, sortOptions) {
      const filteredKey = Object.fromEntries(
        Object.entries(sortOptions).filter(([key]) => item === key),
      )
      const sortValue = filteredKey[item] || 200
      return sortValue
    },
    getColumnTitle(gridColumns, fieldName, defaultTitle) {
      if (gridColumns.length === 0) {
        return defaultTitle
      }
      const [first] = gridColumns.filter(item => item.field === fieldName)
      if (first) {
        return first.title
      }
      return defaultTitle
    },
    isColumnVisible(gridColumns, fieldName) {
      if (gridColumns.length === 0) {
        return true
      }
      const [first] = gridColumns.filter(item => item.field === fieldName)
      if (first) {
        return first.visible !== undefined ? first.visible : true
      }
      return false
    },
    isColumnDrillSupported(gridColumns, fieldName) {
      if (gridColumns.length === 0) {
        return false
      }
      const [first] = gridColumns.filter(item => item.field === fieldName)
      if (first) {
        return first.drill
      }
      return false
    },
    resetLocalStorage(localStorageName) {
      const items = JSON.parse(localStorage.getItem(localStorageName))
      const loadedItems = items === null ? [] : items
      const filteredItems = loadedItems.filter(item => {
        const itemDate = item.date
        const currentDate = new Date()
        return isDate1AndDate2LessThanDays(itemDate, currentDate, 120)
      })
      const parsed = JSON.stringify(filteredItems)
      localStorage.setItem(localStorageName, parsed)
    },
    updateLocalStorage(localStorageName, updateDateFieldName, event) {
      const items = JSON.parse(localStorage.getItem(localStorageName))
      const loadedItems = items === null ? [] : items
      const item = {
        id: event.value,
        date: event.data[updateDateFieldName],
      }
      loadedItems.push(item)
      const parsed = JSON.stringify(loadedItems)
      localStorage.setItem(localStorageName, parsed)
      const params = {
        force: true,
        suppressFlash: true,
      }
      this.gridApi.refreshCells(params)
    },
  },
}
</script>
